import React, { useCallback, useEffect, useState } from "react";
import { Search, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { adminApi } from "@/lib/admin";
import { absoluteUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AiInsights() {
  const [searchParams] = useSearchParams();
  const userFilter = searchParams.get("user") || "";
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (userFilter) params.user_id = userFilter;
    adminApi.get("/ai-analyses", { params }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [q, userFilter]);

  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <div className="sq-label flex items-center gap-1"><Sparkles size={12} /> AI Insights</div>
        <h1 className="text-4xl font-heading font-bold mt-1 text-secondary">AI analysis log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {total} analyses saved — review uploaded images and AI JSON to improve prompts
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by factory name, phone" className="h-12 pl-10" />
      </div>

      <div className="space-y-4">
        {items.length === 0 && (
          <div className="sq-card p-10 text-center text-muted-foreground">
            No AI analyses yet. They appear when users tap &quot;Analyze design (AI)&quot;.
          </div>
        )}
        {items.map((a) => {
          const r = a.result || {};
          const open = expanded === a.id;
          return (
            <div key={a.id} className="sq-card overflow-hidden">
              <div className="p-4 flex gap-4">
                <img
                  src={absoluteUrl(a.image_url)}
                  alt=""
                  className="w-28 h-28 rounded-lg object-cover bg-muted shrink-0 cursor-pointer"
                  onClick={() => window.open(absoluteUrl(a.image_url), "_blank")}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold">{a.business_name || a.user_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{a.user_phone} · lang: {a.lang}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {a.created_at?.slice(0, 16).replace("T", " ")}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${
                      r.complexity === "complex" ? "border-destructive/30 text-destructive" :
                      r.complexity === "simple" ? "border-success/30 text-success" :
                      "border-warning/30 text-warning"
                    }`}>
                      {r.complexity || "—"}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                    <Stat label="Stitches" value={Number(r.stitch_count || 0).toLocaleString("en-IN")} />
                    <Stat label="Time" value={`${r.machine_time_minutes || 0} min`} />
                    <Stat label="Colors" value={r.thread_colors || 0} />
                  </div>

                  {(r.bits_details || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.bits_details.map((b, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-accent/20 border border-accent/30">
                          {b.type}{b.approx_count ? ` · ${b.approx_count}pcs` : ""}{b.approx_grams ? ` · ${b.approx_grams}g` : ""}
                        </span>
                      ))}
                    </div>
                  )}

                  {r.notes && <p className="text-xs mt-2 text-muted-foreground line-clamp-2">{r.notes}</p>}
                </div>
              </div>

              <div className="border-t border-border px-4 py-2 bg-muted/30">
                <Button variant="ghost" size="sm" className="w-full justify-between h-9"
                  onClick={() => setExpanded(open ? null : a.id)}>
                  {open ? "Hide" : "Show"} full AI JSON
                  {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </Button>
                {open && (
                  <pre className="mt-2 p-3 rounded-lg bg-surface border border-border text-xs overflow-auto max-h-96 whitespace-pre-wrap">
                    {JSON.stringify(r, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/40 p-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
